import React, { useCallback, useMemo, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGalleryConciergeProducts } from './useGalleryConciergeProducts';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { ConciergeProduct } from '@apps/card/concierge/conciergeProduct.types';
import { isConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { ConciergeProductDialog } from '@apps/card/concierge/ConciergeProductDialog';
import type { CustomizeNowClickOverrideHandler } from '@apps/card/types/stationeryTemplate.types';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import type { CardEventType } from './components/GalleryItem/GalleryItem';

interface SelectedConciergeProductState {
  readonly selectedConciergeProductSortIndex: number | undefined;
  readonly selectedConciergeProduct: ConciergeProduct | undefined;
}

export const useConciergeDisclosure = (onCustomizeCardClickOverride?: CustomizeNowClickOverrideHandler) => {
  const { isOpen, onOpen: openDisclosure, onClose: closeDisclosure } = useDisclosure();
  const { currentCategory } = useCardCategoryContext();
  const { goToConcierge } = useCardsRouterContext();
  const { conciergeProducts } = useGalleryConciergeProducts();
  const [selectedConciergeProduct, setSelectedConciergeProduct] = useState<SelectedConciergeProductState>({
    selectedConciergeProductSortIndex: undefined,
    selectedConciergeProduct: undefined,
  });

  const getSelectedConciergeProduct = useCallback(
    (id: string, category: StationeryTemplateCategoryEnum): SelectedConciergeProductState => {
      if (!isConciergeServiceCategory(category)) {
        return { selectedConciergeProductSortIndex: -1, selectedConciergeProduct: undefined };
      }
      const sortIndex = conciergeProducts[category].findIndex(product => product.id === id);
      return {
        selectedConciergeProductSortIndex: sortIndex,
        selectedConciergeProduct: conciergeProducts[category][sortIndex],
      };
    },
    [conciergeProducts],
  );

  const onCardSelect = useEventCallback(
    (event: CardEventType) => {
      if (event.type === 'conciergeselectedevent') {
        setSelectedConciergeProduct(getSelectedConciergeProduct(event.id, currentCategory));
        openDisclosure();
      }
    },
    [getSelectedConciergeProduct, currentCategory, openDisclosure],
  );

  const onCustomizeClick = useEventCallback((product: ConciergeProduct) => {
    if (onCustomizeCardClickOverride) {
      onCustomizeCardClickOverride(
        {
          format: 'concierge',
          template: { ...product, thumbnailUrl: product.imageUrl },
          priceConfigId: '',
        },
        closeDisclosure,
      );
    } else {
      goToConcierge(product.identifier);
    }
  });

  const ConciergeDialog = useMemo(() => {
    if (!isConciergeServiceCategory(currentCategory)) return null;

    return (
      <ConciergeProductDialog
        onCustomizeConciergeProductClick={onCustomizeClick}
        category={currentCategory}
        isOpen={isOpen}
        onClose={closeDisclosure}
        selectedConciergeProduct={selectedConciergeProduct.selectedConciergeProduct}
        conciergeProductSortIndex={selectedConciergeProduct.selectedConciergeProductSortIndex!}
        orderedProducts={conciergeProducts[currentCategory]}
      />
    );
  }, [currentCategory, isOpen, closeDisclosure, onCustomizeClick, selectedConciergeProduct, conciergeProducts]);

  return { ConciergeDialog, onCardSelect };
};
